import React from "react";
import { useTable, Column } from "react-table";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  Invoice,
  InvoiceStatus,
  invoiceStatusToText,
  InvoiceType,
  invoiceTypeToText,
  OwesReportItem,
} from "../../../../models";
import { getInvoicePDF, resetInvoicePDF, cancelInvoice, resetCancelInvoice, resendInvoice, resetResendInvoice, getInvoices } from "../../ordersSlice";
import { base64toBlobPdf } from "../../../../lib/base64ToBlobPdf";
import { CancelInvoiceModal } from "../CancelInvoiceModal";
import { setShowStaxPaymentModal } from "../../../Payments/paymentsSlice";
import { StaxPaymentModal } from "../../../Payments/modals/StaxPaymentModal/StaxPaymentModal";

import "./InvoicesTable.scss";

type ColumnsType = Invoice;

export function InvoicesTable() {
  const dispatch = useAppDispatch();

  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = React.useState<Invoice | null>(null);

  const invoices = useAppSelector((state) => state.orders.invoices);
  const order = useAppSelector((state) => state.orders.order);
  const getInvoicePDFAPI = useAppSelector(
    (state) => state.orders.getInvoicePDFAPI
  );
  const cancelInvoiceAPI = useAppSelector(
    (state) => state.orders.cancelInvoiceAPI
  );
  const markPaidPaymentsAPI = useAppSelector(
    (state) => state.payments.markPaidAPI
  );
  const resendInvoiceAPI = useAppSelector(
    (state) => state.orders.resendInvoiceAPI
  );

  const renderActionsCell = (props: any) => {
    const invoice = props.row.original;
    const canCancel = invoice.status !== InvoiceStatus.CANCELED && invoice.status !== InvoiceStatus.PAID;
    const canCharge = invoice.status !== InvoiceStatus.CANCELED && invoice.status !== InvoiceStatus.PAID;
    const canResend = invoice.status !== InvoiceStatus.CANCELED;

    return (
      <div className="actions-cell">
        <div
          className="view-button"
          onClick={onViewInvoiceClick(invoice.id)}
        >
          View
        </div>
        {canResend && (
          <div
            className="resend-button"
            onClick={onResendInvoiceClick(invoice.id)}
          >
            Resend
          </div>
        )}
        {canCharge && (
          <div
            className="charge-button"
            onClick={onChargeClick(invoice)}
          >
            Charge
          </div>
        )}
        {canCancel && (
          <div
            className="cancel-button"
            onClick={onCancelInvoiceClick(invoice)}
          >
            Cancel
          </div>
        )}
      </div>
    );
  };

  const onViewInvoiceClick = (invoiceId: string) => () => {
    dispatch(getInvoicePDF({ id: invoiceId }));
  };

  const onResendInvoiceClick = (invoiceId: string) => () => {
    if (resendInvoiceAPI.loading) return;
    dispatch(resendInvoice({ id: invoiceId }));
  };

  const onCancelInvoiceClick = (invoice: Invoice) => () => {
    if (cancelInvoiceAPI.loading) return;
    setInvoiceToCancel(invoice);
    setShowCancelModal(true);
  };

  const onChargeClick = (invoice: Invoice) => () => {
    // Convert Invoice to OwesReportItem format for the modal
    // Ensure total is a number (it may come as a string from the API)
    const owesReportItem: OwesReportItem = {
      invoiceId: invoice.id,
      orderId: invoice.orderId,
      name: order?.name || "",
      total: typeof invoice.total === 'string' ? parseFloat(invoice.total) : invoice.total,
      type: invoice.type,
    };

    dispatch(setShowStaxPaymentModal(owesReportItem));
  };

  const handleConfirmCancel = () => {
    if (invoiceToCancel) {
      dispatch(cancelInvoice({ id: invoiceToCancel.id }));
    }
  };

  const handleCloseModal = () => {
    setShowCancelModal(false);
    setInvoiceToCancel(null);
  };

  React.useEffect(() => {
    if (!getInvoicePDFAPI.loading && !!getInvoicePDFAPI.value) {
      const pdf = base64toBlobPdf(getInvoicePDFAPI.value);
      const fileURL = URL.createObjectURL(pdf);
      const pdfWindow = window.open();
      pdfWindow!.location.href = fileURL;

      dispatch(resetInvoicePDF());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInvoicePDFAPI.loading, !!getInvoicePDFAPI.value]);

  // Refresh invoices when payment is successful
  React.useEffect(() => {
    if (!markPaidPaymentsAPI.loading && !!markPaidPaymentsAPI.value) {
      if (order?.id) {
        dispatch(getInvoices({ orderId: order.id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markPaidPaymentsAPI.loading, !!markPaidPaymentsAPI.value]);

  React.useEffect(() => {
    if (!cancelInvoiceAPI.loading && !!cancelInvoiceAPI.error) {
      toast.error("Failed to cancel invoice. Please try again.");
      dispatch(resetCancelInvoice());
      handleCloseModal();
    }

    if (!cancelInvoiceAPI.loading && !!cancelInvoiceAPI.value) {
      toast.success("Invoice cancelled successfully.");
      dispatch(resetCancelInvoice());
      handleCloseModal();

      // Refresh invoices
      if (order?.id) {
        dispatch(getInvoices({ orderId: order.id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelInvoiceAPI.loading, !!cancelInvoiceAPI.error, !!cancelInvoiceAPI.value]);

  React.useEffect(() => {
    if (!resendInvoiceAPI.loading && !!resendInvoiceAPI.error) {
      toast.error("Failed to resend invoice. Please try again.");
      dispatch(resetResendInvoice());
    }

    if (!resendInvoiceAPI.loading && !!resendInvoiceAPI.value) {
      toast.success("Invoice resent successfully.");
      dispatch(resetResendInvoice());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendInvoiceAPI.loading, !!resendInvoiceAPI.error, !!resendInvoiceAPI.value]);

  const columns: Array<Column<ColumnsType>> = React.useMemo(
    () => [
      {
        Header: "Type",
        accessor: (cell) => invoiceTypeToText[cell.type as InvoiceType],
      },
      {
        Header: "Status",
        accessor: (cell) => invoiceStatusToText[cell.status as InvoiceStatus],
      },
      {
        Header: "Amount",
        accessor: (cell) => `$${cell.total}`,
      },
      {
        Header: "Actions",
        accessor: "id",
        width: 220,
        maxWidth: 220,
        Cell: renderActionsCell,
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: invoices });

  if (invoices.length === 0)
    return (
      <div className="invoices-table-component empty-state">
        <div className="text">There are no invoices.</div>
      </div>
    );

  return (
    <>
      <div className="invoices-table-component">
        <table {...getTableProps()} className="new-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps({
                      style: { minWidth: column.minWidth, width: column.width },
                    })}
                  >
                    <div className="header-cell">{column.render("Header")}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CancelInvoiceModal
        isOpen={showCancelModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmCancel}
        loading={cancelInvoiceAPI.loading}
        invoiceType={invoiceToCancel ? invoiceTypeToText[invoiceToCancel.type as InvoiceType] : undefined}
        invoiceAmount={invoiceToCancel?.total}
      />

      <StaxPaymentModal />
    </>
  );
}
